import { PageHero, SitePage } from "@/components/SitePage";
import HomeClosingCTA from "@/components/HomeClosingCTA";

const metadata = [
  ["Sector", "Steel manufacturing"],
  ["Area", "Order operations"],
  ["Scope", "AI order capture · WhatsApp live, voice in build"],
  ["Stage", "Live · Expanding"],
];

const stats = [
  ["5,000+", "orders captured every month"],
  ["150", "SKUs across the product catalogue"],
  ["2", "languages plus trade shorthand"],
  ["2", "channels: WhatsApp live, phone calls next"],
];

const sections = [
  ["01", "Operating requirement", [
    "A steel manufacturer receives dealer orders through WhatsApp as handwritten images containing product shorthand, dimensions, gauges, lengths, quantities, units, rates and notes, spanning a catalogue of around 150 SKUs.",
    "English, Bengali and trade abbreviations often appear in the same order. At more than 5,000 orders a month, the operational challenge is to reconstruct every order accurately without losing the context experienced staff use during manual entry.",
  ]],
  ["02", "Delivered system", [
    "Averentia delivered an AI order-capture system with WhatsApp as its first channel. It converts each image into a complete, editable order draft, captures every visible detail, links terms to the SKU reference and flags missing or uncertain values for review.",
    "The review screen keeps the source image, structured draft and manual entry together. An authorised user resolves differences and approves the order before it becomes an ERP draft or operational record.",
  ]],
  ["03", "Operational controls", [
    "Each structured draft is compared with the completed manual entry, giving the team a consistent record of matches, corrections and unresolved values.",
    "Quality is measured at the whole-order level, not through a small set of easy fields. Reporting covers match rate, field accuracy, time per order, corrections and recurring failure cases.",
  ]],
  ["04", "Next channel: phone calls", [
    "In the ongoing engagement, Averentia is adding a voice channel that extracts orders directly from dealer phone calls. Calls arrive in the same mix of languages and shorthand as the WhatsApp images and are converted into the same structured draft.",
    "Both channels feed one review queue, one SKU reference and one set of quality controls, so the order-capture system grows across channels without duplicating the operating process.",
  ]],
];

export default function SteelCaseStudy() {
  return (
    <SitePage>
      <PageHero
        label="Case study"
        title="AI order capture for a steel manufacturer."
        intro="A multichannel order-capture system that turns dealer orders into structured, verified ERP drafts: more than 5,000 handwritten WhatsApp orders a month across 150 SKUs today, with phone-call orders being added next."
        aside={
          <figure className="av-page-image">
            <img src="/case-studies/steel-manufacturing.jpg" alt="Steelworkers overseeing an industrial steel furnace" />
            <figcaption>Photo: Kateryna Babaieva / Pexels</figcaption>
          </figure>
        }
      >
        <a className="av-back-link" href="/work">← All work</a>
      </PageHero>
      <section className="av-case-metadata" aria-label="Project information">
        <div className="av-shell av-metadata-grid">
          {metadata.map(([label, value]) => (
            <div key={label}><span>{label}</span><strong>{value}</strong></div>
          ))}
        </div>
      </section>
      <section className="av-case-stats" aria-label="Results">
        <div className="av-shell av-stats-grid">
          {stats.map(([value, label]) => (
            <div key={label}><strong>{value}</strong><span>{label}</span></div>
          ))}
        </div>
      </section>
      <section className="av-section av-page-section">
        <div className="av-shell av-case-layout">
          <aside>
            <p className="av-section-label"><span /> Engagement scope</p>
            <p>Handwriting, mixed-language shorthand, SKU matching, uncertainty review, whole-order evaluation and a second channel for phone-call orders.</p>
          </aside>
          <div className="av-case-body">
            {sections.map(([number, title, paragraphs]) => (
              <article key={number}>
                <span className="av-index">{number}</span>
                <h2>{title}</h2>
                {paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              </article>
            ))}
          </div>
        </div>
      </section>
      <HomeClosingCTA />
    </SitePage>
  );
}
